import React, { useState } from "react";
import {
  Box,
  Button,
  Popover,
  TextField,
  Typography,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import { DateTime } from "luxon";

type CsvDateExportProps = {
  /** ダウンロードハンドラ。startDate/endDate は yyyy-MM-dd 形式 */
  onDownload: (startDate: string, endDate: string) => Promise<void> | void;
  /** ボタンラベル（デフォルト: CSVダウンロード） */
  label?: string;
  /** 期間の最大日数（デフォルト: 31） */
  maxDays?: number;
};

/**
 * 期間指定 CSV ダウンロード共通コンポーネント（ポップオーバー版）。
 *
 * [📥 CSVダウンロード ▾] ← クリックでポップオーバーが開き、期間を選んで DL。
 *
 * DayNav と組み合わせて使う。日次表示は DayNav、長期データは CSV で。
 * サーバー負荷を防ぐため、最大日数を超えた指定はエラーで弾く。
 */
const CsvDateExport: React.FC<CsvDateExportProps> = ({
  onDownload,
  label = "CSVダウンロード",
  maxDays = 31,
}) => {
  const today = DateTime.local().toFormat("yyyy-MM-dd");
  const monthAgo = DateTime.local()
    .minus({ days: maxDays - 1 })
    .toFormat("yyyy-MM-dd");

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [startDate, setStartDate] = useState<string>(monthAgo);
  const [endDate, setEndDate] = useState<string>(today);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setError("");
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const validate = (s: string, e: string): string => {
    if (!s || !e) return "開始日と終了日を指定してください";
    const start = DateTime.fromISO(s);
    const end = DateTime.fromISO(e);
    if (!start.isValid || !end.isValid) return "日付が不正です";
    if (start > end) return "開始日は終了日より前にしてください";
    const diff = end.diff(start, "days").days + 1;
    if (diff > maxDays) return `期間は${maxDays}日以内で指定してください`;
    return "";
  };

  const handleDownload = async () => {
    const err = validate(startDate, endDate);
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setIsLoading(true);
    try {
      await onDownload(startDate, endDate);
      setAnchorEl(null);
    } catch (e) {
      setError("ダウンロードに失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        color="primary"
        startIcon={<GetAppIcon />}
        onClick={handleOpen}
      >
        {label}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box display="flex" flexDirection="column" p={2} style={{ gap: 8 }}>
          <Typography style={{ fontSize: 13, fontWeight: "bold" }}>
            期間指定 CSV 出力
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <TextField
              type="date"
              size="small"
              label="開始日"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ width: 150 }}
            />
            <Typography style={{ fontSize: 13 }}>〜</Typography>
            <TextField
              type="date"
              size="small"
              label="終了日"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ width: 150 }}
            />
          </Box>
          <Typography style={{ fontSize: 12, color: "#999" }}>
            （最大{maxDays}日）
          </Typography>
          {error && (
            <Typography style={{ fontSize: 12, color: "#e53935" }}>
              {error}
            </Typography>
          )}
          <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
            <Button onClick={handleClose} size="small">
              キャンセル
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<GetAppIcon />}
              onClick={handleDownload}
              disabled={isLoading}
            >
              {isLoading ? "出力中..." : "ダウンロード"}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default CsvDateExport;
